import styled from 'styled-components';
import tw from 'twin.macro';
import { useLocation, useNavigate } from 'react-router-dom';
import Logo from '../logo';
import NavItems from './navItems';
import { NAV_BAR_HEIGHT_PX } from './constants';
import { SCREENS } from '../responsive';
import { useSetSearchNavSlot } from './searchNavSlot';
import { useSnapshot } from 'valtio';
import { Role, roleStore } from '../../../store/roleStore';
import { isServicesSite, isStaysSite } from '../../constants/siteConfig';

const NavBarWrapper = styled.header<{ $light?: boolean }>`
  ${tw`w-full max-w-full min-w-0 shrink-0 relative z-50 overflow-x-hidden box-border`}
  background-color: ${({ $light }) =>
    $light ? 'var(--color-bg-elevated, #fff)' : 'var(--color-black)'};
  border-bottom: ${({ $light }) => ($light ? '1px solid var(--color-border)' : 'none')};
  height: ${NAV_BAR_HEIGHT_PX}px;
  max-height: ${NAV_BAR_HEIGHT_PX}px;
  overflow: visible;
`;

const NavBarContainer = styled.div<{ $fullBleed?: boolean; $wide?: boolean }>`
  height: ${NAV_BAR_HEIGHT_PX}px;
  margin: 0 auto;
  padding-top: 0;
  ${tw`
    w-full
    box-border
    flex
    flex-row
    items-center
    justify-between
  `};
  max-width: ${({ $fullBleed }) => ($fullBleed ? '100%' : '1200px')};
  padding-left: ${({ $fullBleed }) => ($fullBleed ? '12px' : '16px')};
  padding-right: ${({ $fullBleed }) => ($fullBleed ? '12px' : '16px')};

  @media (min-width: ${SCREENS.md}) {
    padding-left: ${({ $fullBleed }) => ($fullBleed ? '20px' : '32px')};
    padding-right: ${({ $fullBleed }) => ($fullBleed ? '20px' : '32px')};
  }

  @media (min-width: ${SCREENS.lg}) {
    padding-left: ${({ $fullBleed }) => ($fullBleed ? '24px' : '0')};
    padding-right: ${({ $fullBleed }) => ($fullBleed ? '24px' : '0')};
  }

  /* Stays guest header: Airbnb's wide page gutter. */
  ${({ $wide }) =>
    $wide &&
    `
    max-width: 1760px;
    padding-left: 24px;
    padding-right: 24px;
    @media (min-width: ${SCREENS.md}) {
      padding-left: 40px;
      padding-right: 40px;
    }
  `}
`;

const LogoContainer = styled.div`
  ${tw`flex items-center cursor-pointer shrink-0`}
`;

const SearchSlot = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  margin: 0 10px;
`;

const NavActions = styled.div`
  ${tw`shrink-0`}
`;

function NavBar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const setSearchSlot = useSetSearchNavSlot();
  const { role } = useSnapshot(roleStore);
  // Stays (spec 016) and services (spec 019) guests get the white header; hosts and every
  // car site keep the dark one.
  const light = (isStaysSite() || isServicesSite()) && role !== Role.HOST && role !== Role.ADMIN;
  const isSearchResults =
    pathname.startsWith('/cars/guest/search') || pathname.startsWith('/stays/search');

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <NavBarWrapper $light={light}>
      <NavBarContainer $fullBleed={isSearchResults} $wide={light}>
        <LogoContainer onClick={handleLogoClick}>
          <Logo
            color={light ? 'dark' : 'white'}
            bgColor={light ? 'white' : 'dark'}
            hideTextBelowMd={isSearchResults}
          />
        </LogoContainer>
        {isSearchResults && <SearchSlot ref={setSearchSlot} />}
        <NavActions>
          <NavItems tone={light ? 'light' : 'dark'} />
        </NavActions>
      </NavBarContainer>
    </NavBarWrapper>
  );
}

export default NavBar;
